import React, {
    useEffect,
    useRef,
    useState,
    useLayoutEffect,
    useMemo,
} from "react";
import {
    StyledContainer,
    CarouselContainer,
    StyledImageWrapper,
    StyledImage,
    ImageText,
    ProgressBarWrapper,
    ProgressBar,
    StyledOverlay,
    ImageBulletPoint,
    BulletPointContainer,
} from "./StyledComponents";
import StackedGallery from "./StackedGallery/StackedGallery";
import convertS3ImageUrl from "../../assets/store/convertS3ImageUrl";

const ImageGallery = (props) => {
    const {
        image_gallery,
        divId,
        rowId,
        rowData,
        defaultFontFamily,
        isTableRow,
        oem,
        client,
    } = props;
    const { isMobile } = window?.window?.deviceDetect?.getWindowData() || {};
    const carouselRef = useRef(null);
    const firstImageRef = useRef(null); // only need ref from 1 image to get dimensions
    const containerRef = useRef(null);

    // window width to render stackGallery, if not a table row always render swipe image gallery
    const renderStackWidth = isTableRow ? 600 : 100000000;
    const [windowWidth, setWindowWidth] = useState(null);
    const [activeImageIndex, setActiveImageIndex] = useState(0); // only applicable for mobile...

    const [isDragging, setIsDragging] = useState(false);
    const [prevPageX, setPrevPageX] = useState(null);
    const [prevScrollLeft, setPrevScrollLeft] = useState(null);
    const [progressPercent, setProgressPercent] = useState(0);

    // autoplay variables
    const autoplayTimerRef = useRef(null); // autoplay timer reference
    const lastInteractionRef = useRef(null); // last interaction timer ref
    const imagesLength =
        rowData?.image_gallery?.gallery_type === "six_image" ? 6 : 9;

    const marginLeft = 0;
    const imagesPerPage = {
        // how many images on the page depending on screen size... change the devisor to set number of photos in carousel at once
        large: 5,
        medium: 3,
        small: 1,
    };

    const { primary_button_color } = oem || {};

    const {
        background_color,
        caption_overlay_color,
        caption_text_color,
        gallery_type,
        header_text,
        image_1_caption_text,
        image_1_open_in_new_tab,
        image_1_open_link,
        image_1_url,
        image_1_open_link_id,
        selected_image_1_open_link,
        image_2_caption_text,
        image_2_open_in_new_tab,
        image_2_open_link,
        image_2_url,
        image_2_open_link_id,
        selected_image_2_open_link,
        image_3_caption_text,
        image_3_open_in_new_tab,
        image_3_open_link,
        image_3_url,
        image_3_open_link_id,
        selected_image_3_open_link,
        image_4_caption_text,
        image_4_open_in_new_tab,
        image_4_open_link,
        image_4_url,
        image_4_open_link_id,
        selected_image_4_open_link,
        image_5_caption_text,
        image_5_open_in_new_tab,
        image_5_open_link,
        image_5_url,
        image_5_open_link_id,
        selected_image_5_open_link,
        image_6_caption_text,
        image_6_open_in_new_tab,
        image_6_open_link,
        image_6_url,
        image_6_open_link_id,
        selected_image_6_open_link,
        image_7_caption_text,
        image_7_open_in_new_tab,
        image_7_open_link,
        image_7_url,
        image_7_open_link_id,
        selected_image_7_open_link,
        image_8_caption_text,
        image_8_open_in_new_tab,
        image_8_open_link,
        image_8_url,
        image_8_open_link_id,
        selected_image_8_open_link,
        image_9_caption_text,
        image_9_open_in_new_tab,
        image_9_open_link,
        image_9_url,
        image_9_open_link_id,
        selected_image_9_open_link,
    } = image_gallery;

    const dataAttributes = {
        "data-clientid": client.id,
        "data-divid": divId,
        "data-rowid": rowId,
        "data-gallerytype": gallery_type,
        "data-header": header_text,
        "data-galleryid": image_gallery.id,
        "data-ascmediatype": "image",
    };

    const images = useMemo(() => {
        // use reduce to iterate thru the 9 images
        const imagesUnconverted = Array.from({ length: 9 }).reduce(
            (acc, _, index) => {
                const image = {
                    text: eval(`image_${index + 1}_caption_text`),
                    open: eval(`selected_image_${index + 1}_open_link`)
                        ?.open_link_in_new_tab
                        ? "_blank"
                        : eval(`image_${index + 1}_open_in_new_tab`)
                        ? "_blank"
                        : "_self",
                    link: eval(`image_${index + 1}_open_link_id`)
                        ? eval(`selected_image_${index + 1}_open_link`)
                        : eval(`image_${index + 1}_open_link`),
                    url: eval(`image_${index + 1}_url`),
                };
                return [...acc, image];
            },
            []
        );

        const convertedImages = imagesUnconverted.map((image) => {
            image.url = convertS3ImageUrl(image.url, {
                resize: { width: isMobile ? 300 : 600 },
            });

            return image;
        });

        return convertedImages;
    }, []);

    useLayoutEffect(() => {
        const windowWidth = document.documentElement.clientWidth;
        setWindowWidth(windowWidth);
    }, []);

    useEffect(() => {
        const windowWidth = document.documentElement.clientWidth;
        const parentNode = containerRef?.current?.parentNode?.parentNode;
        if (!parentNode) return;

        if (windowWidth < renderStackWidth) {
            parentNode.style.gridTemplateColumns = "1fr";
        } else {
            const numberOfRows = parentNode.getAttribute("data-numberofrows");
            parentNode.style.gridTemplateColumns = "1fr ".repeat(numberOfRows);
        }

        // stop auto play if stack gallery is rendered
        if (windowWidth <= renderStackWidth) {
            stopAutoplay();
            stopLastInteraction();
        }
    }, [windowWidth]);

    useEffect(() => {
        // on component load: set progress bar percent & start interaction timer
        const carousel = carouselRef.current;
        const firstImage = firstImageRef.current;
        if (!carousel || !firstImage) return;
        const imageWidth = firstImage.clientWidth + marginLeft;
        const maxWidth = carousel.scrollWidth - carousel.clientWidth;
        updateProgressBar(((0 + imageWidth) / (maxWidth + imageWidth)) * 100);
        startLastInteraction();

        // center carousel images on window resize
        window.addEventListener("resize", autoCenter);
        return () => {
            window.removeEventListener("resize", autoCenter);
            stopAutoplay();
            stopLastInteraction();
        };
    }, []);

    const startAutoplay = () => {
        stopAutoplay();

        autoplayTimerRef.current = window.setInterval(() => {
            const breakPoints = getBreakPoints();
            const carousel = carouselRef.current;
            const maxWidth = carousel.scrollWidth - carousel.clientWidth;

            let nextIndex;

            // find closest breakpoint to current scroll position of carousel container
            const closest = breakPoints.reduce((prev, curr) => {
                const currentCalc = Math.abs(curr - carousel.scrollLeft);
                const prevCalc = Math.abs(prev - carousel.scrollLeft);
                return currentCalc < prevCalc ? curr : prev;
            });

            // based on closest breakpoint find index of that breakpoint then increment
            breakPoints.forEach((breakpoint, index) => {
                if (breakpoint === closest)
                    nextIndex = index + 1 <= imagesLength - 1 ? index + 1 : 0;
                // if carousel is currently scrolled to its max set scroll position to start (sometimes container is off by 1 or 2 pixels hence other checks)
                if (
                    carousel.scrollLeft === maxWidth ||
                    carousel.scrollLeft === maxWidth - 1 ||
                    carousel.scrollLeft === maxWidth - 2 ||
                    carousel.scrollLeft === maxWidth - 3
                )
                    nextIndex = 0;
            });

            autoCenter(breakPoints[nextIndex]);
        }, 4000);
    };

    const stopAutoplay = () => {
        if (autoplayTimerRef.current) {
            window.clearInterval(autoplayTimerRef.current);
            autoplayTimerRef.current = null;
        }
    };

    const startLastInteraction = () => {
        // start autoplaying image gallery if it has not been interacted with in x miliseconds
        if (autoplayTimerRef.current) return;
        stopLastInteraction();

        const windowWidth = document.documentElement.clientWidth;
        if (windowWidth >= renderStackWidth) return;

        lastInteractionRef.current = setTimeout(() => {
            startAutoplay();
            stopLastInteraction(); // stop timer if autoplay timer starts
        }, 4000);
    };

    const stopLastInteraction = () => {
        if (lastInteractionRef.current) {
            window.clearTimeout(lastInteractionRef.current);
            lastInteractionRef.current = null;
        }
    };

    const updateProgressBar = (percentOverride) => {
        const carousel = carouselRef.current;
        const firstImage = firstImageRef.current;
        if (!carousel || !firstImage) return;

        const imageWidth = firstImage.clientWidth + marginLeft;

        const maxWidth = carousel.scrollWidth - carousel.clientWidth;
        // scrollLeft starts at 0... need to add image width to account for initial position
        const percentScrolled =
            percentOverride || percentOverride === 0
                ? percentOverride
                : Math.ceil(
                      ((carousel.scrollLeft + imageWidth) /
                          (maxWidth + imageWidth)) *
                          100
                  );

        setProgressPercent(percentScrolled);
    };

    const getBreakPoints = () => {
        // set breakpoints of where each image's width ends/begins
        const windowWidth = document.documentElement.clientWidth;
        if (windowWidth >= renderStackWidth) return;

        const firstImage = firstImageRef.current;
        if (!firstImage) return;
        const imageWidth = firstImage.clientWidth + marginLeft; // add margin from StyledImageWrapper to image width

        const breakPoints = [];
        images.forEach((image, index) => breakPoints.push(imageWidth * index));
        return breakPoints;
    };

    const autoCenter = (targetPosition = null) => {
        const windowWidth = document.documentElement.clientWidth;
        setWindowWidth(windowWidth);
        if (windowWidth >= renderStackWidth) return;

        const carousel = carouselRef.current;
        const firstImage = firstImageRef.current;
        if (!carousel || !firstImage) return;
        const imageWidth = firstImage.clientWidth + marginLeft;
        const maxWidth = carousel.scrollWidth - carousel.clientWidth;

        // if there is no image left to scroll then return
        if (carousel.scrollLeft === maxWidth && targetPosition === null)
            return updateProgressBar();

        const breakPoints = getBreakPoints();
        // find closest scrollLeft position based on image sizing... auto center to closest match
        const closest = breakPoints.reduce((prev, curr) => {
            const currentCalc = Math.abs(
                curr -
                    (targetPosition || targetPosition === 0
                        ? targetPosition
                        : carousel.scrollLeft)
            );
            const prevCalc = Math.abs(
                prev -
                    (targetPosition || targetPosition === 0
                        ? targetPosition
                        : carousel.scrollLeft)
            );
            return currentCalc < prevCalc ? curr : prev;
        });

        carousel.scrollLeft = closest;

        // smooth scroll animation takes a few frames to end... need to calc here instead of in updateProgressBar method
        const percentScrolled = Math.ceil(
            ((closest + imageWidth) / (maxWidth + imageWidth)) * 100
        );
        updateProgressBar(percentScrolled);

        /* get index of image and set as active */
        const activeIndex = breakPoints.findIndex(
            (breakPoint) => breakPoint === closest
        );
        setActiveImageIndex(activeIndex);
    };

    const handleDrag = (event) => {
        if (!isDragging) return;
        const carousel = carouselRef.current;
        const posDifference =
            (event.pageX || event.touches[0].pageX) - prevPageX;
        carousel.scrollLeft = prevScrollLeft - posDifference;
    };

    const dragStart = (event) => {
        const carousel = carouselRef.current;
        carousel.style.setProperty("scroll-behavior", "auto");

        setIsDragging(true);
        setPrevPageX(event.pageX || event.touches[0].pageX);
        setPrevScrollLeft(carousel.scrollLeft);

        stopAutoplay();
        stopLastInteraction();
    };

    const dragStop = () => {
        const carousel = carouselRef.current;
        carousel.style.setProperty("scroll-behavior", "smooth");
        setIsDragging(false);

        autoCenter();
        startLastInteraction();
    };

    const handleLinkClick = (event, image) => {
        if (!image || image.link === "") return;
        const { target } = event;
        // stop clicks until faded in... stops user from accidentally clicking when invisible
        const opacity = window
            .getComputedStyle(target)
            .getPropertyValue("opacity");
        if (isDragging || opacity < "0.60") return;
        window.open(
            image.link,
            image.open_link_in_new_tab ? "_blank" : "_self"
        );
    };

    if (windowWidth > renderStackWidth) {
        return (
            <StackedGallery
                {...props}
                imagesUnconverted={images}
                containerRef={containerRef}
                dataAttributes={dataAttributes}
            />
        );
    }

    return (
        <StyledContainer
            className="FODImageGalleryContainer"
            defaultFontFamily={defaultFontFamily}
            ref={containerRef}
        >
            {windowWidth + 15 > 550 ? (
                <ProgressBarWrapper>
                    <ProgressBar
                        bgColor={primary_button_color}
                        progressPercent={progressPercent}
                    />
                </ProgressBarWrapper>
            ) : null}
            <CarouselContainer
                className="FODImageGallery"
                ref={carouselRef}
                onMouseMove={handleDrag}
                onMouseDown={(event) => dragStart(event)}
                onMouseUp={dragStop}
                onMouseLeave={dragStop}
                // mobile
                onTouchMove={handleDrag}
                onTouchStart={(event) => dragStart(event)}
                onTouchEnd={dragStop}
                bgColor={rowData.background_color}
            >
                {images.map((image, index) => {
                    if (!image.url) return;
                    const specificAttributes = {
                        "data-imagenumber": index + 1,
                        "data-caption": image.text,
                        "data-imageurl": image.url,
                    };

                    const gaAttributes = {
                        "data-clickindex": index + 1,
                        "data-imagegalleryid": rowData.image_gallery.id,
                        "data-imageurl": image.url,
                        "data-clickurl": image.link,
                        "data-ascmediatype": "image",
                    };
                    return (
                        <StyledImageWrapper
                            className="FODGAImageGalleryItem"
                            ref={index === 0 ? firstImageRef : null}
                            marginLeft={marginLeft}
                            imagesPerPage={imagesPerPage}
                            key={`image-gallery-image-${index}`}
                        >
                            <StyledOverlay
                                className="ImageOverlay"
                                hasLink={
                                    image.link &&
                                    !!(typeof image.link === "object"
                                        ? image.link.link
                                        : image.link)
                                }
                            />
                            <ImageText
                                {...gaAttributes}
                                {...specificAttributes}
                                {...dataAttributes}
                                className="FODGAImageGalleryLink FODV5GAImageGalleryClickEvent"
                                linkHoverColor={rowData.link_hover_color}
                                linkColor={rowData.link_color}
                                bgColor={rowData.background_color}
                                link={image.link}
                                onClick={(event) =>
                                    handleLinkClick(
                                        event,
                                        typeof image.link === "object"
                                            ? image.link
                                            : image
                                    )
                                }
                            >
                                {image.text}
                            </ImageText>
                            <StyledImage
                                draggable={false}
                                src={image.url}
                                marginLeft={marginLeft}
                                imagesPerPage={imagesPerPage}
                                hasLink={
                                    image.link &&
                                    !!(typeof image.link === "object"
                                        ? image.link.link
                                        : image.link)
                                }
                            />
                        </StyledImageWrapper>
                    );
                })}
            </CarouselContainer>
            {windowWidth + 15 <= 550 ? (
                <BulletPointContainer>
                    {images.map((image, index) => {
                        const breakPoints = getBreakPoints();

                        return (
                            <ImageBulletPoint
                                onClick={() => {
                                    autoCenter(breakPoints[index]);
                                    stopAutoplay();
                                    startLastInteraction();
                                }}
                                backgroundColor={primary_button_color}
                                isActive={activeImageIndex === index}
                                key={"image-gallery-pagination-point" + index}
                            />
                        );
                    })}
                </BulletPointContainer>
            ) : null}
        </StyledContainer>
    );
};

export default ImageGallery;
